/**
 * Chat Widget Component
 * 
 * Floating chat widget with lighthouse icon
 * Handles conversation with backend API
 */

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Cookies from 'js-cookie';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// import { v4 as uuidv4 } from 'uuid'; // Removed as backend handles GUID generation if needed
import chatApi, { startSession } from '../../lib/chatApi';
import LighthouseIcon from '../LighthouseIcon/LighthouseIcon';
import './ChatWidget.scss';

// Augure returns markdown (bold, lists, links). Assistant replies are rendered
// through react-markdown so they display formatted instead of as raw `**`/`-`.
// Links are forced to open in a new tab — the widget commonly runs inside an
// iframe, so same-tab navigation would blow away the host page. User messages
// are NOT parsed as markdown (no reason to format user input; avoids surprises).
const markdownComponents = {
    a: ({ node, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
    ),
};

// e.g. <ChatWidget disclaimer="Independent Chestnut Holler local guide — not official tourism. Hours and events change; verify before you go." />
const ChatWidget = forwardRef(function ChatWidget({ brandId, brandName = 'Lighthouse', scripted = null, disclaimer = null, autoOpen = false }, ref) {
    const [isOpen, setIsOpen] = useState(!!scripted || autoOpen);
    // Check if running in iframe
    const isEmbedded = window.self !== window.top;
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSessionReady, setIsSessionReady] = useState(false);
    const [brandGreeting, setBrandGreeting] = useState(`Hello! Welcome to ${brandName}. How can I help you today?`);
    const messagesEndRef = useRef(null);

    // Scripted mode: render a fixed conversation with no backend session.
    // Used by the marketing screenshot harness (/shot/:bizSlug).
    useEffect(() => {
        if (scripted) {
            setMessages(scripted);
        }
    }, [scripted]);

    // Initialize Session (Auth Handshake)
    useEffect(() => {
        const initSession = async () => {
            try {
                // Sanitize brandId for cookie name (spaces not allowed)
                const safeBrandId = brandId.replace(/\s+/g, '_');
                const cookieName = `lighthouse_guid_${safeBrandId}`;
                let storedGuid = Cookies.get(cookieName);

                // Start session with backend (exchanges brandId/guid for signed JWT)
                const session = await startSession(brandId, storedGuid);

                // Update cookie with confirmed/new GUID
                if (session.guid) {
                    Cookies.set(cookieName, session.guid, {
                        expires: 365,
                        secure: window.location.protocol === 'https:',
                        sameSite: 'Strict'
                    });
                }

                // Use server-provided greeting if available
                if (session.brand?.greeting) {
                    setBrandGreeting(session.brand.greeting);
                }

                setIsSessionReady(true);
                loadHistory(session.brand?.greeting); // Token is now set in chatApi
            } catch (error) {
                console.error('Failed to initialize chat session:', error);
                // Don't enable chat if auth fails
            }
        };

        if (brandId && !scripted) {
            initSession();
        }
    }, [brandId, scripted]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        // Instant scroll in scripted mode so headless screenshots capture the full conversation
        messagesEndRef.current?.scrollIntoView({ behavior: scripted ? 'auto' : 'smooth' });
    };

    // Notify parent window (for iframe resizing)
    useEffect(() => {
        const message = {
            type: 'lighthouse-resize',
            isOpen: isOpen
        };
        window.parent.postMessage(message, '*');
    }, [isOpen]);

    const loadHistory = async (greeting) => {
        const welcomeMessage = greeting || brandGreeting;
        try {
            const response = await chatApi.get('/chat/history');

            if (response.data.success && response.data.messages) {
                if (response.data.messages.length > 0) {
                    setMessages(response.data.messages);
                } else {
                    // Start with welcome message for new sessions
                    setMessages([{
                        role: 'assistant',
                        content: welcomeMessage,
                        timestamp: new Date(),
                    }]);
                }
            }
        } catch (error) {
            console.error('Failed to load chat history:', error);
            // Start with welcome message
            setMessages([
                {
                    role: 'assistant',
                    content: welcomeMessage,
                    timestamp: new Date(),
                },
            ]);
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();

        if (!inputMessage.trim() || isLoading || !isSessionReady) return;

        const userMessage = {
            role: 'user',
            content: inputMessage,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        try {
            const response = await chatApi.post('/chat/message', {
                message: inputMessage,
            });

            if (response.data.success) {
                const aiMessage = {
                    role: 'assistant',
                    content: response.data.response,
                    timestamp: new Date(),
                };

                setMessages(prev => [...prev, aiMessage]);
            } else {
                throw new Error('Failed to get response');
            }
        } catch (error) {
            console.error('Error sending message:', error);

            const errorMessage = {
                role: 'assistant',
                content: "I'm having trouble connecting right now. Please try again in a moment.",
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleWidget = () => {
        setIsOpen(!isOpen);
    };

    // Expose open method to parent via ref
    useImperativeHandle(ref, () => ({
        open: () => setIsOpen(true),
    }));

    return (
        <div className={`chat-widget ${isEmbedded ? 'embedded' : ''} ${isOpen ? 'open' : ''}`}>
            {/* Chat Window */}
            {isOpen && (
                <div className="chat-window">
                    {/* Header */}
                    <div className="chat-header bg-lighthouse-gradient text-white">
                        <div className="d-flex align-items-center">
                            <LighthouseIcon size={40} isActive={true} />
                            <div className="ms-3">
                                <h6 className="mb-0 fw-bold">{brandName}</h6>
                                <small className="text-white-50">Customer Service</small>
                            </div>
                        </div>
                        <button
                            className="btn-close btn-close-white"
                            onClick={toggleWidget}
                            aria-label="Close chat"
                        ></button>
                    </div>

                    {/* Messages */}
                    <div className="chat-messages">
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`message ${msg.role === 'user' ? 'message-user' : 'message-assistant'}`}
                            >
                                <div className="message-content">
                                    {msg.role === 'user' ? (
                                        msg.content
                                    ) : (
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={markdownComponents}
                                        >
                                            {msg.content}
                                        </ReactMarkdown>
                                    )}
                                </div>
                                <div className="message-timestamp">
                                    {new Date(msg.timestamp).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>
                        ))}

                        {isLoading && (
                            <div className="message message-assistant">
                                <div className="message-content">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="chat-input">
                        <form onSubmit={sendMessage} className="d-flex">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Type your message..."
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                disabled={isLoading}
                            />
                            <button
                                type="submit"
                                className="btn btn-primary ms-2"
                                disabled={isLoading || !inputMessage.trim()}
                            >
                                Send
                            </button>
                        </form>
                    </div>

                    {/* Attribution */}
                    <div className="chat-footer">
                        {disclaimer && (
                            <p className="chat-footer-disclaimer">{disclaimer}</p>
                        )}
                        Powered by{' '}
                        <a
                            href="https://augureai.ca"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Augure
                        </a>
                        {' '}— Sovereign Canadian AI
                    </div>
                </div>
            )}

            {/* Floating Button */}
            <button
                className="chat-toggle-btn"
                onClick={toggleWidget}
                aria-label={isOpen ? 'Close chat' : 'Open chat'}
            >
                {isOpen ? (
                    <span className="fs-4">✕</span>
                ) : (
                    <LighthouseIcon size={60} isActive={true} />
                )}
            </button>
        </div>
    );
});

export default ChatWidget;
